import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n';
import './index.css';
import App from './App';
import { ThemeProvider } from './contexts/ThemeContext';
import { initTextScaleObserver } from './utils/textScale';
import { initKeyboardScrollAssist } from './utils/keyboardScroll';
import { installAuthStateInterceptor } from './utils/authState';
import { anchorLaunchHistory } from './utils/launchBackAnchor';

installAuthStateInterceptor();
anchorLaunchHistory();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </React.StrictMode>
);

// Accessibility: detect device font magnification / browser zoom and add
// body classes so overflow-prone containers reveal auto-hide scrollbars.
initTextScaleObserver();

// Accessibility: scroll focused text inputs above the on-screen keyboard
// so users can always see what they're typing.
initKeyboardScrollAssist();

// Despia safe area: Auto Inject is ON in Despia settings — Despia handles safe area natively.
// No extra padding from our side. Only the statusbarcolor command for theme matching.
// Despia: lock body scroll to prevent rubber-banding, add class for CSS targeting
if (navigator.userAgent.toLowerCase().includes('despia')) {
  document.body.style.overflow = 'hidden';
  document.body.classList.add('despia-native');
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('iphone')) {
    document.body.classList.add('despia-iphone');
    // Detect keyboard open/close to toggle bottom spacing
    if (window.visualViewport) {
      const initialHeight = window.visualViewport.height;
      window.visualViewport.addEventListener('resize', () => {
        const keyboardOpen = window.visualViewport.height < initialHeight - 100;
        document.body.classList.toggle('keyboard-open', keyboardOpen);
      });
    }
  } else if (ua.includes('android')) {
    // Despia Android — distinct from despia-iphone / despia-ipad. The
    // previous code mis-tagged Android Despia as `despia-ipad` because
    // of the bare `else` fallthrough, which meant Android Despia
    // styles never matched the iPad-only rules and the status-bar
    // safe-area padding was silently skipped.
    document.body.classList.add('despia-android');
    // Mirror the keyboard-open detection from iPhone so chat input
    // bars behave the same way on Android Despia.
    if (window.visualViewport) {
      const initialHeight = window.visualViewport.height;
      window.visualViewport.addEventListener('resize', () => {
        const keyboardOpen = window.visualViewport.height < initialHeight - 100;
        document.body.classList.toggle('keyboard-open', keyboardOpen);
      });
    }
  } else {
    document.body.classList.add('despia-ipad');
  }
} else if (/android/i.test(navigator.userAgent)) {
  // Android native app — lock body scroll to prevent pull-to-refresh,
  // same approach as Despia iOS. Scroll happens inside page-content / ScrollArea.
  document.body.style.overflow = 'hidden';
  document.body.classList.add('android-native');
} else if (window.navigator.standalone === true) {
  // iOS Safari PWA (Add to Home Screen) — standalone mode.
  document.body.classList.add('ios-pwa-standalone');
  // Detect keyboard open/close to toggle bottom spacing
  if (window.visualViewport) {
    const initialHeight = window.visualViewport.height;
    window.visualViewport.addEventListener('resize', () => {
      const keyboardOpen = window.visualViewport.height < initialHeight - 100;
      document.body.classList.toggle('keyboard-open', keyboardOpen);
    });
  }
}

// Despia: set status bar color on app startup (before React mounts)
// This ensures the status bar matches the theme immediately on launch
(function initStatusBar() {
  if (!navigator.userAgent.toLowerCase().includes('despia')) return;
  try {
    // Read saved color mode and theme from localStorage
    const colorMode = localStorage.getItem('eili_color_mode') || 'dark';
    // Default backgrounds per mode
    const defaultBg = colorMode === 'light' ? '#F1F5F9' : '#0F172A';
    const hex = defaultBg.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    const ua = navigator.userAgent.toLowerCase();
    const isAndroid = ua.includes('android');

    import('despia-native').then(({ default: despia }) => {
      despia(`statusbarcolor://{${r}, ${g}, ${b}}`);
      if (isAndroid) {
        despia(colorMode === 'light' ? 'statusbartextcolor://black' : 'statusbartextcolor://white');
      }
    }).catch(() => {
      try { window.despia = `statusbarcolor://{${r}, ${g}, ${b}}`; } catch(e) {}
      if (isAndroid) {
        try { window.despia = colorMode === 'light' ? 'statusbartextcolor://black' : 'statusbartextcolor://white'; } catch(e) {}
      }
    });
  } catch(e) {}
})();

// Universal viewport height fix — runs on ALL devices.
// CSS 100dvh/100vh can differ from the actual visible area on mobile/tablet.
// This sets --app-height to the real pixel height, updated on resize.
(function fixViewportHeight() {
  const set = () => {
    document.documentElement.style.setProperty('--app-height', window.innerHeight + 'px');
    // Expose the *visual* viewport height too. On iOS, when the soft
    // keyboard opens, window.innerHeight stays at the layout-viewport
    // size while window.visualViewport.height shrinks. Floating UI
    // elements (popovers, sheets) that need to stay fully reachable
    // — including those triggered from the chat composer while the
    // keyboard is open — should size against `--visual-vh`, not
    // `--app-height` or `100vh`. Falls back to innerHeight when
    // visualViewport is unavailable (older browsers, some WebViews).
    const vv = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    document.documentElement.style.setProperty('--visual-vh', vv + 'px');
  };
  set();
  window.addEventListener('resize', set);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', set);
    // visualViewport `scroll` events also fire as the keyboard
    // dismisses/reveals on iOS — keep the var in sync.
    window.visualViewport.addEventListener('scroll', set);
  }
})();


// Mobile WebView "blank white screen on resume" workaround.
// Symptom: user backgrounds the app and returns to a blank white
// screen; the content snaps back as soon as they tap. Root cause is
// the WebView compositor losing its surface while the page is in the
// background, so when it's resumed it has a stale empty layer until
// something invalidates it. Both iOS Safari (when running as a PWA
// or inside Despia) and some Android Despia builds exhibit this.
//
// Fix: on visibilitychange→visible (and the older `pageshow` event),
// force the compositor to repaint by toggling a no-op CSS transform
// on the root for one frame. This is cheap, invisible, and reliable.
(function forceRepaintOnResume() {
  const repaint = () => {
    try {
      const root = document.body || document.documentElement;
      if (!root) return;
      // Force a synchronous style recalculation without visual change.
      // `translateZ(0)` promotes the layer; setting back to ''
      // synchronously invalidates the cached layer the WebView is
      // still showing. Wrapped in rAF so we don't fight the
      // browser's own visibility-restore work.
      root.style.transform = 'translateZ(0)';
      requestAnimationFrame(() => {
        root.style.transform = '';
      });
    } catch (e) {
      // Defensive — never let the repaint nudge break the app.
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Two frames — gives the WebView time to surface its layers
      // before we invalidate, otherwise the repaint can be skipped.
      requestAnimationFrame(() => requestAnimationFrame(repaint));
    }
  });
  // iOS Safari fires `pageshow` with `persisted=true` when restoring
  // from the back/forward cache (a common path that bypasses the
  // visibility events). Treat that as a resume too.
  window.addEventListener('pageshow', (ev) => {
    if (ev.persisted) {
      requestAnimationFrame(() => requestAnimationFrame(repaint));
    }
  });
})();

// Android Despia safe-area fallback — Android WebView does NOT
// populate `env(safe-area-inset-*)` unless the wrapper opts in to
// display-cutout mode AND sets `viewport-fit=cover`. Despia Android
// also doesn't always inject `--safe-area-top` like iOS does. We
// detect "unset on Android Despia" and seed sane defaults so the
// status bar (~24dp) and gesture nav bar (~16dp) don't overlap the
// app chrome. Despia/native CSS-var injections still win — we only
// set our defaults when the var resolves to 0px or empty.
(function seedAndroidDespiaSafeArea() {
  const ua = navigator.userAgent.toLowerCase();
  if (!ua.includes('despia') || !ua.includes('android')) return;
  // Run after a tick so any Despia-injected CSS vars have a chance
  // to land first; our defaults only kick in when Despia hasn't set
  // its own (which is the symptom the user reported).
  setTimeout(() => {
    try {
      const cs = getComputedStyle(document.documentElement);
      const measure = (name) => parseFloat(cs.getPropertyValue(name)) || 0;
      const topNow = measure('--safe-area-top') || measure('--sat');
      const bottomNow = measure('--safe-area-bottom') || measure('--sab');
      // Standard Android status bar = 24dp; gesture nav bar = 16dp.
      // We use a device-pixel-ratio-aware fudge so the spacing reads
      // right on dense displays without overshooting on small ones.
      const dpr = window.devicePixelRatio || 1;
      const baseTop = dpr >= 2.5 ? 44 : 36;
      const baseBottom = dpr >= 2.5 ? 48 : 32;
      const root = document.documentElement;
      if (topNow < baseTop) {
        root.style.setProperty('--safe-area-top', baseTop + 'px');
        root.style.setProperty('--sat', baseTop + 'px');
      }
      if (bottomNow < baseBottom) {
        root.style.setProperty('--safe-area-bottom', baseBottom + 'px');
        root.style.setProperty('--sab', baseBottom + 'px');
      }
    } catch { /* ignore */ }
  }, 50);
})();

// Disable pinch-zoom on web (iOS Safari/Chrome, Android Chrome, Android native
// WebView, and iOS Safari PWA). Native iOS wrapper (Despia) already disables
// this at the native layer, but we still apply the same guard there since it
// has no side effects. The viewport meta `user-scalable=no` works on Android
// and older iOS, but iOS 10+ intentionally ignores it — so we also block the
// gesture events in JS for robust coverage everywhere.
(function disablePinchZoom() {
  // Block gesture events (iOS-specific but harmless on Android)
  const prevent = (e) => e.preventDefault();
  document.addEventListener('gesturestart', prevent, { passive: false });
  document.addEventListener('gesturechange', prevent, { passive: false });
  document.addEventListener('gestureend', prevent, { passive: false });

  // Block multi-touch pinch on all platforms
  document.addEventListener('touchmove', (e) => {
    if (e.touches && e.touches.length > 1) e.preventDefault();
  }, { passive: false });

  // Block iOS Safari double-tap zoom
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 350) e.preventDefault();
    lastTouchEnd = now;
  }, { passive: false });

  // Block Ctrl+wheel zoom (desktop pinch on trackpad / Cmd+wheel on Mac).
  // We mark this listener as passive on desktop where blocking the wheel
  // listener is unnecessary AND interferes with smooth scrolling; only
  // mobile native wrappers actually need the synchronous block. On
  // mobile (Despia / android-native), the body class is set so we can
  // use that to conditionally upgrade the listener to non-passive.
  const isMobileNative = navigator.userAgent.toLowerCase().includes('despia')
    || /(android.*wv|; wv\))/i.test(navigator.userAgent);
  document.addEventListener('wheel', (e) => {
    if (e.ctrlKey && isMobileNative) e.preventDefault();
  }, { passive: !isMobileNative });
})();


// Service Worker update checks (registration is in index.html for fast PWABuilder detection)
// Skip inside Despia native wrapper — service workers conflict with WKWebView
const isDespia = navigator.userAgent.toLowerCase().includes('despia');
if ('serviceWorker' in navigator && !isDespia) {
  navigator.serviceWorker.ready.then((reg) => {
    // Check for updates immediately
    reg.update();
    
    // Check for updates every 30 minutes (more frequent than hourly)
    setInterval(() => reg.update(), 30 * 60 * 1000);
    
    // Check for updates when app becomes visible (user returns to tab/app)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reg.update();
    });
    
    // If a worker is already waiting (from index.html detection), activate it
    if (reg.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Handle update found - activate new SW immediately
    reg.addEventListener('updatefound', () => {
      const newWorker = reg.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      }
    });
  });

  // NOTE: Auto-reload on controllerchange DISABLED per user request.
  // New service worker versions still activate (skipWaiting + clients.claim),
  // but the page no longer forcibly reloads itself. Users will see fresh
  // assets on their next natural navigation/refresh, or via the manual
  // "Check for Updates" button in Settings (exposed as
  // window.__eiliForceRefresh below).
}

// -----------------------------------------------------------------------------
// Version-check heartbeat: DISABLED per user request (2026-02-24).
// The heartbeat previously auto-nuked (unregistered SW, wiped caches,
// hard-reloaded) whenever /api/app-version reported a different version
// than the session baseline. That behavior caused user-visible flicker
// and, when combined with a stale baseline bug, an infinite reload loop.
//
// We keep `window.__eiliForceRefresh` available as a MANUAL trigger so the
// Settings → "Check for Updates" button still works. Nothing runs on a
// timer, on visibility change, or on version mismatch anymore.
// -----------------------------------------------------------------------------
(function appVersionHeartbeat() {
  if (typeof window === 'undefined') return;
  // Skip inside Despia native wrapper — the wrapper handles its own refresh.
  if (navigator.userAgent.toLowerCase().includes('despia')) return;

  async function nuke() {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
    } finally {
      // Hard reload bypassing cache
      window.location.reload();
    }
  }

  // Expose ONLY as a manual trigger for the Settings → "Check for Updates"
  // button. No timers, no visibility listeners, no version-mismatch nuke.
  window.__eiliForceRefresh = nuke;
})();